import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { DataEmissaoSegura, erroDataEmissao } from './DataEmissaoSegura';
import {
  Send, ArrowLeft, Loader2, CheckCircle, FileText, Filter, RefreshCw, AlertCircle, Eye, Building2
} from 'lucide-react';

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const CAMPOS_DATA = [
  { value: 'competencia', label: 'Competência' },
  { value: 'vencimento', label: 'Vencimento' },
  { value: 'pagamento', label: 'Pagamento' },
  { value: 'autorizacao', label: 'Autorização' },
  { value: 'emissao', label: 'Emissão' },
];

const SITUACOES_NFSE = ['pendente', 'processando', 'autorizada', 'rejeitada', 'cancelada'];
const CICLOS = ['Educação Infantil', 'Fundamental I', 'Fundamental II', 'Ensino Médio'];

const FILTROS_INICIAIS = {
  competencia_mes: String(new Date().getMonth() + 1),
  competencia_ano: String(new Date().getFullYear()),
  data_campo: 'competencia',
  data_inicio: '',
  data_fim: '',
  busca: '',
  sacado: 'ambos',
  situacao: 'todas',
  tipo_recebimento: 'todos',
  referencia_inicio: '',
  referencia_fim: '',
  situacao_nfse: 'todas',
  numero_rps: '',
  numero_nfse: '',
  turma_id: 'todas',
  vinculo_turma: 'atuais',
  valor_min: '',
  valor_max: '',
  ciclo: 'todos',
  somente_turmas_ativas: true,
  somente_titulos_turma: false,
  prestador_id: 'config_principal',
};

const fmtData = (v) => {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (v || '-');
};
const brl = (v) => (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

export default function FiscalEmissao() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [titulos, setTitulos] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [tiposRecebimento, setTiposRecebimento] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [catFiltro, setCatFiltro] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);

  const [usarManual, setUsarManual] = useState(false);
  const [textoManual, setTextoManual] = useState('');

  const [emitindo, setEmitindo] = useState(false);
  const [emitindoMes, setEmitindoMes] = useState(false);
  const [previa, setPrevia] = useState(null);
  const [previaAberta, setPreviaAberta] = useState(false);
  const [previaAlvo, setPreviaAlvo] = useState('selecionados');
  const [dataEmissao, setDataEmissao] = useState('');
  const [dialogAberto, setDialogAberto] = useState(false);
  const [resultadoEmissao, setResultadoEmissao] = useState(null);

  const set = (campo, valor) => setFiltros(prev => ({ ...prev, [campo]: valor }));

  const montarParams = useCallback(() => {
    const p = {
      data_campo: filtros.data_campo,
      sacado: filtros.sacado,
      situacao: filtros.situacao,
      vinculo_turma: filtros.vinculo_turma,
      somente_titulos_turma: filtros.somente_titulos_turma,
      prestador_id: filtros.prestador_id,
    };
    if (filtros.competencia_mes !== 'todos') p.competencia_mes = Number(filtros.competencia_mes);
    if (filtros.competencia_ano) p.competencia_ano = Number(filtros.competencia_ano);
    if (filtros.data_inicio) p.data_inicio = filtros.data_inicio;
    if (filtros.data_fim) p.data_fim = filtros.data_fim;
    if (filtros.busca.trim()) p.busca = filtros.busca.trim();
    if (filtros.tipo_recebimento !== 'todos') p.tipo_recebimento = filtros.tipo_recebimento;
    if (filtros.referencia_inicio) p.referencia_inicio = filtros.referencia_inicio;
    if (filtros.referencia_fim) p.referencia_fim = filtros.referencia_fim;
    if (filtros.situacao_nfse !== 'todas') p.situacao_nfse = filtros.situacao_nfse;
    if (filtros.numero_rps.trim()) p.numero_rps = filtros.numero_rps.trim();
    if (filtros.numero_nfse.trim()) p.numero_nfse = filtros.numero_nfse.trim();
    if (filtros.turma_id !== 'todas') p.turma_id = filtros.turma_id;
    if (String(filtros.valor_min).trim() !== '') p.valor_min = Number(filtros.valor_min);
    if (String(filtros.valor_max).trim() !== '') p.valor_max = Number(filtros.valor_max);
    if (filtros.ciclo !== 'todos') p.ciclo = filtros.ciclo;
    if (catFiltro.length) p.categorias = catFiltro.join(',');
    return p;
  }, [filtros, catFiltro]);

  const buscar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/fiscal/titulos-elegiveis', { params: montarParams() });
      setTitulos(data.titulos || []);
      setTiposRecebimento(data.tipos_recebimento || []);
      if (!catFiltro.length) setCategorias(data.categorias || []);
      setResumo({
        total_elegiveis: data.total_elegiveis ?? 0,
        total_bloqueados: data.total_bloqueados ?? 0,
        valor_elegivel: data.valor_elegivel ?? 0,
        bloqueios: data.bloqueios || {},
        prestador: data.prestador,
      });
      setSelecionados([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar títulos');
    } finally {
      setLoading(false);
    }
  }, [montarParams, catFiltro.length]);

  useEffect(() => { buscar(); /* eslint-disable-next-line */ }, []);

  // Valor e ciclo re-filtram sozinhos (sem reload), com debounce curto
  const primeiraCarga = React.useRef(true);
  useEffect(() => {
    if (primeiraCarga.current) { primeiraCarga.current = false; return; }
    const t = setTimeout(() => buscar(), 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [filtros.valor_min, filtros.valor_max, filtros.ciclo]);

  useEffect(() => {
    api.get('/fiscal/empresas').then(r => setEmpresas(r.data.empresas || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/fiscal/turmas', { params: { somente_ativas: filtros.somente_turmas_ativas } })
      .then(r => setTurmas(r.data.turmas || [])).catch(() => {});
  }, [filtros.somente_turmas_ativas]);

  const toggleCategoria = (key) =>
    setCatFiltro(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const selecionarCategoria = (key) => {
    const ids = titulos.filter(t => !t.bloqueio && t.categoria_key === key).map(t => t.id);
    setSelecionados(prev => Array.from(new Set([...prev, ...ids])));
    toast.success(`${ids.length} título(s) selecionado(s)`);
  };

  const toggleSelecionado = (id) =>
    setSelecionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const elegiveisVisiveis = titulos.filter(t => !t.bloqueio);
  const toggleTodos = () =>
    setSelecionados(prev => prev.length === elegiveisVisiveis.length ? [] : elegiveisVisiveis.map(t => t.id));

  const totalSelecionado = titulos
    .filter(t => selecionados.includes(t.id))
    .reduce((s, t) => s + (t.valor_pago || t.valor_recebido || 0), 0);

  const corpoEmissao = (alvo) => ({
    competencia_mes: filtros.competencia_mes !== 'todos' ? Number(filtros.competencia_mes) : null,
    competencia_ano: filtros.competencia_ano ? Number(filtros.competencia_ano) : null,
    categorias: catFiltro.length ? catFiltro : null,
    titulo_ids: alvo === 'selecionados' ? selecionados : null,
    transmitir: true,
    discriminacao: usarManual && textoManual.trim() ? textoManual.trim() : null,
    prestador_id: filtros.prestador_id,
    filtros: montarParams(),
    data_emissao_rps: dataEmissao || null,
  });
  const msgErro = (e, padrao) => {
    const d = e.response?.data?.detail;
    return (typeof d === 'string' ? d : d?.detail) || padrao;
  };

  const abrirPrevia = async (alvo) => {
    if (alvo === 'selecionados' && !selecionados.length) {
      toast.error('Selecione pelo menos um título');
      return;
    }
    setPreviaAlvo(alvo);
    try {
      const { data } = await api.post('/fiscal/notas/previa-lote', corpoEmissao(alvo));
      setPrevia(data);
      setDataEmissao(data.emissao?.data_sugerida_br || '');
      setPreviaAberta(true);
    } catch (e) {
      toast.error(msgErro(e, 'Não foi possível montar a prévia'));
    }
  };

  const confirmarEmissao = async () => {
    const alvo = previaAlvo;
    const erroData = erroDataEmissao(dataEmissao, previa?.emissao);
    if (erroData) { toast.error(erroData); return; }
    alvo === 'selecionados' ? setEmitindo(true) : setEmitindoMes(true);
    try {
      const { data } = await api.post('/fiscal/notas/emitir-lote-competencia', corpoEmissao(alvo));
      setResultadoEmissao({
        ...(data.resultados || {}),
        transmissao: data.transmissao,
        credenciais: data.credenciais,
        valor_total: data.valor_total,
      });
      setPreviaAberta(false);
      setDialogAberto(true);
      buscar();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.data_segura) setPrevia((p) => ({ ...p, emissao: d.emissao || p?.emissao }));
      toast.error(msgErro(e, 'Erro ao emitir notas'));
    } finally {
      setEmitindo(false);
      setEmitindoMes(false);
    }
  };

  const empresaAtual = empresas.find(e => e.id === filtros.prestador_id);

  const anos = [];
  for (let a = new Date().getFullYear() + 1; a >= new Date().getFullYear() - 4; a--) anos.push(a);

  return (
    <div className="p-6 space-y-6" data-testid="fiscal-emissao">
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/fiscal')} data-testid="fiscal-voltar">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Send className="w-6 h-6" />
            Emissão de Notas Fiscais
          </h1>
          <p className="text-muted-foreground">Filtre os títulos pagos e emita as NFS-e</p>
          {resumo && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs" data-testid="fiscal-resumo-elegiveis">
              <span className="rounded-full bg-emerald-50 px-3 py-1 font-bold text-emerald-700">
                {resumo.total_elegiveis} elegíveis · R$ {brl(resumo.valor_elegivel)}
              </span>
              <span className="rounded-full bg-amber-50 px-3 py-1 font-bold text-amber-700">
                {resumo.total_bloqueados} com bloqueio
              </span>
              {Object.entries(resumo.bloqueios || {}).map(([k, v]) => (
                <span key={k} className="rounded-full bg-slate-100 px-3 py-1 text-slate-600">
                  {String(k).replace(/_/g, ' ')}: {v}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* FILTROS */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">

          {/* DATA */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Data</Label>
            <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-5">
              <div className="space-y-1">
                <Label className="text-xs">Campo</Label>
                <Select value={filtros.data_campo} onValueChange={(v) => set('data_campo', v)}>
                  <SelectTrigger data-testid="filtro-data-campo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CAMPOS_DATA.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Período inicial</Label>
                <Input type="date" value={filtros.data_inicio}
                  onChange={(e) => set('data_inicio', e.target.value)} data-testid="filtro-data-inicio" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Período final</Label>
                <Input type="date" value={filtros.data_fim}
                  onChange={(e) => set('data_fim', e.target.value)} data-testid="filtro-data-fim" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Competência (mês)</Label>
                <Select value={filtros.competencia_mes} onValueChange={(v) => set('competencia_mes', v)}>
                  <SelectTrigger data-testid="filtro-competencia-mes"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os meses</SelectItem>
                    {MESES.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Competência (ano)</Label>
                <Select value={filtros.competencia_ano} onValueChange={(v) => set('competencia_ano', v)}>
                  <SelectTrigger data-testid="filtro-competencia-ano"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {anos.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* SACADO + SITUAÇÃO */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Sacado</Label>
              <Select value={filtros.sacado} onValueChange={(v) => set('sacado', v)}>
                <SelectTrigger data-testid="filtro-sacado"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aluno">Aluno</SelectItem>
                  <SelectItem value="responsavel">Responsável/Cliente</SelectItem>
                  <SelectItem value="ambos">Ambos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Buscar sacado</Label>
              <Input placeholder="Nome ou CPF/CNPJ" value={filtros.busca}
                onChange={(e) => set('busca', e.target.value)} data-testid="filtro-busca" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Situação</Label>
              <Select value={filtros.situacao} onValueChange={(v) => set('situacao', v)}>
                <SelectTrigger data-testid="filtro-situacao"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="geradas">Geradas</SelectItem>
                  <SelectItem value="nao_geradas">Não geradas</SelectItem>
                  <SelectItem value="todas">Todas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Empresa emissora</Label>
              <Select value={filtros.prestador_id} onValueChange={(v) => set('prestador_id', v)}>
                <SelectTrigger data-testid="filtro-prestador"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(empresas.length ? empresas : [{ id: 'config_principal', razao_social: 'Empresa principal' }])
                    .map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nome_fantasia || p.razao_social || p.id}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {empresaAtual && (
                <p className="text-[11px] text-muted-foreground" data-testid="empresa-resumo">
                  CNPJ {empresaAtual.cnpj} · IM {empresaAtual.inscricao_municipal || '-'} ·
                  {' '}série RPS {empresaAtual.serie_rps || '-'} · {empresaAtual.ambiente}
                  {empresaAtual.optante_simples ? ' · Simples Nacional' : ''}
                  {empresaAtual.credenciais_configuradas ? '' : ' · credenciais faltando'}
                </p>
              )}
            </div>
          </div>

          {/* FINANCEIRO */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Financeiro</Label>
            <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="space-y-1">
                <Label className="text-xs">Tipo de recebimento</Label>
                <Select value={filtros.tipo_recebimento} onValueChange={(v) => set('tipo_recebimento', v)}>
                  <SelectTrigger data-testid="filtro-tipo-recebimento"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {tiposRecebimento.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Referência de</Label>
                <Input type="month" value={filtros.referencia_inicio}
                  onChange={(e) => set('referencia_inicio', e.target.value)} data-testid="filtro-ref-inicio" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Referência até</Label>
                <Input type="month" value={filtros.referencia_fim}
                  onChange={(e) => set('referencia_fim', e.target.value)} data-testid="filtro-ref-fim" />
              </div>
            </div>
          </div>

          {/* VALOR E CICLO */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Valor e ciclo</Label>
            <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="space-y-1">
                <Label className="text-xs">Valor mínimo (R$)</Label>
                <Input type="number" step="0.01" min="0" placeholder="0,00"
                  value={filtros.valor_min} onChange={(e) => set('valor_min', e.target.value)}
                  data-testid="filtro-valor-min" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Valor máximo (R$)</Label>
                <Input type="number" step="0.01" min="0" placeholder="Sem limite"
                  value={filtros.valor_max} onChange={(e) => set('valor_max', e.target.value)}
                  data-testid="filtro-valor-max" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Ciclo</Label>
                <Select value={filtros.ciclo} onValueChange={(v) => set('ciclo', v)}>
                  <SelectTrigger data-testid="filtro-ciclo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os ciclos</SelectItem>
                    {CICLOS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* NOTA FISCAL */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Nota fiscal</Label>
            <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="space-y-1">
                <Label className="text-xs">Situação da NFS-e</Label>
                <Select value={filtros.situacao_nfse} onValueChange={(v) => set('situacao_nfse', v)}>
                  <SelectTrigger data-testid="filtro-situacao-nfse"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas</SelectItem>
                    {SITUACOES_NFSE.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Nº RPS</Label>
                <Input value={filtros.numero_rps} onChange={(e) => set('numero_rps', e.target.value)}
                  data-testid="filtro-numero-rps" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Nº NFS-e</Label>
                <Input value={filtros.numero_nfse} onChange={(e) => set('numero_nfse', e.target.value)}
                  data-testid="filtro-numero-nfse" />
              </div>
            </div>
          </div>

          {/* TURMA */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Turma</Label>
            <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs">Turma</Label>
                <Select value={filtros.turma_id} onValueChange={(v) => set('turma_id', v)}>
                  <SelectTrigger data-testid="filtro-turma"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas as turmas</SelectItem>
                    {turmas.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}{t.year ? ` · ${t.year}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Vínculo com a turma</Label>
                <Select value={filtros.vinculo_turma} onValueChange={(v) => set('vinculo_turma', v)}>
                  <SelectTrigger data-testid="filtro-vinculo-turma"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="atuais">Alunos atuais</SelectItem>
                    <SelectItem value="passaram">Alunos que passaram pela turma</SelectItem>
                    <SelectItem value="ambos">Ambos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={filtros.somente_turmas_ativas}
                  onCheckedChange={(v) => set('somente_turmas_ativas', !!v)}
                  data-testid="filtro-turmas-ativas" />
                Somente turmas ativas
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={filtros.somente_titulos_turma}
                  onCheckedChange={(v) => set('somente_titulos_turma', !!v)}
                  data-testid="filtro-titulos-turma" />
                Somente títulos relacionados à turma
              </label>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t pt-4">
            <Button onClick={buscar} disabled={loading} data-testid="fiscal-filtrar-btn"
              className="bg-slate-900 hover:bg-slate-700">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Filter className="w-4 h-4 mr-2" />}
              FILTRAR
            </Button>
            <Button variant="outline" onClick={() => { setFiltros(FILTROS_INICIAIS); setCatFiltro([]); }}
              data-testid="fiscal-limpar-filtros">
              <RefreshCw className="w-4 h-4 mr-2" />
              Limpar
            </Button>
            <Button variant="outline" onClick={() => navigate('/fiscal/empresas')}
              data-testid="fiscal-configurar-empresas">
              <Building2 className="w-4 h-4 mr-2" />
              Configurar empresas emissoras
            </Button>
            <Button onClick={() => abrirPrevia('todos')}
              disabled={emitindoMes || !(resumo?.total_elegiveis > 0)}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="fiscal-emitir-mes-btn">
              {emitindoMes ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Emitir todas do filtro ({resumo?.total_elegiveis || 0})
            </Button>
          </div>

          {categorias.length > 0 && (
            <div className="border-t pt-4" data-testid="fiscal-categorias">
              <div className="mb-2 flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Categorias</Label>
                {catFiltro.length > 0 && (
                  <button onClick={() => setCatFiltro([])} data-testid="fiscal-categoria-limpar"
                    className="text-xs font-semibold text-slate-500 hover:text-slate-800">
                    limpar categorias
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {categorias.map(cat => {
                  const ativo = catFiltro.includes(cat.categoria_key);
                  return (
                    <div key={cat.categoria_key}
                      className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                        ativo ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'}`}>
                      <button onClick={() => toggleCategoria(cat.categoria_key)} className="font-bold"
                        data-testid={`fiscal-cat-${cat.categoria_key}`}>
                        {cat.categoria_label} · {cat.elegiveis} elegíveis · R$ {brl(cat.valor_elegivel)}
                      </button>
                      {cat.elegiveis > 0 && (
                        <button onClick={() => selecionarCategoria(cat.categoria_key)}
                          data-testid={`fiscal-cat-sel-${cat.categoria_key}`}
                          className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold text-white hover:bg-slate-700">
                          selecionar
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* DISCRIMINAÇÃO */}
      <Card data-testid="fiscal-discriminacao">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Discriminação da NFS-e
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Por padrão o texto é gerado conforme o título e a competência, ex.:{' '}
            <span className="font-medium text-slate-700">
              “{titulos.find(t => t.discriminacao_previa)?.discriminacao_previa
                || 'Mensalidade Escolar referente a outubro de 2026.'}”
            </span>
          </p>
          <label className="flex items-center gap-2 text-sm font-medium">
            <Checkbox checked={usarManual} onCheckedChange={(v) => setUsarManual(!!v)}
              data-testid="discriminacao-usar-manual" />
            Usar descrição manual
          </label>
          {usarManual && (
            <div className="space-y-2">
              <Textarea rows={3} value={textoManual} onChange={(e) => setTextoManual(e.target.value)}
                placeholder="Mensalidade Escolar referente ao mês de outubro de 2026."
                data-testid="discriminacao-textarea" />
              <p className="text-xs text-amber-700">
                Este texto substitui a discriminação <strong>somente desta emissão</strong> — título,
                plano e cadastro não são alterados.
              </p>
              <Button variant="outline" size="sm" onClick={() => abrirPrevia('selecionados')}
                data-testid="discriminacao-aplicar-lote">
                Aplicar este texto a todas as NFS-e selecionadas
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SELEÇÃO */}
      {selecionados.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <CheckCircle className="w-6 h-6 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">{selecionados.length} título(s) selecionado(s)</p>
                  <p className="text-sm text-blue-700">Total: R$ {brl(totalSelecionado)}</p>
                </div>
              </div>
              <Button onClick={() => abrirPrevia('selecionados')} disabled={emitindo}
                className="bg-blue-600 hover:bg-blue-700" data-testid="fiscal-emitir-selecionados">
                {emitindo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                Ver prévia e emitir {selecionados.length} nota(s)
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* TABELA */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Títulos ({titulos.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
          ) : titulos.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhum título encontrado para os filtros aplicados.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selecionados.length === elegiveisVisiveis.length && elegiveisVisiveis.length > 0}
                        onCheckedChange={toggleTodos} data-testid="fiscal-sel-todos" />
                    </TableHead>
                    <TableHead>Aluno</TableHead>
                    <TableHead>Responsável</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead className="text-right">Valor Pago</TableHead>
                    <TableHead>Data Pgto</TableHead>
                    <TableHead>NFS-e / RPS</TableHead>
                    <TableHead>Situação fiscal</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {titulos.map(titulo => (
                    <TableRow key={titulo.id} className={selecionados.includes(titulo.id) ? 'bg-blue-50' : ''}>
                      <TableCell>
                        <Checkbox disabled={!!titulo.bloqueio} checked={selecionados.includes(titulo.id)}
                          onCheckedChange={() => toggleSelecionado(titulo.id)}
                          data-testid={`fiscal-sel-${titulo.id}`} />
                      </TableCell>
                      <TableCell className="font-medium">{titulo.aluno_nome || '-'}</TableCell>
                      <TableCell>{titulo.responsavel_nome || titulo.tomador_nome || '-'}</TableCell>
                      <TableCell>{titulo.descricao || '-'}</TableCell>
                      <TableCell>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                          {titulo.categoria_label || 'Sem categoria'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        R$ {brl(titulo.valor_pago ?? titulo.valor_recebido)}
                      </TableCell>
                      <TableCell>{fmtData(titulo.data_pagamento)}</TableCell>
                      <TableCell className="text-xs">
                        {titulo.nfse_numero ? `NF ${titulo.nfse_numero}` : '—'}
                        {titulo.nfse_rps ? ` · RPS ${titulo.nfse_rps}` : ''}
                        {titulo.nfse_status && (
                          <span className="ml-1 rounded bg-slate-100 px-1 py-0.5 text-[10px] uppercase text-slate-600">
                            {titulo.nfse_status}
                          </span>
                        )}
                      </TableCell>
                      <TableCell data-testid={`fiscal-situacao-${titulo.id}`}>
                        {titulo.bloqueio ? (
                          <div className="space-y-0.5">
                            <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">
                              {String(titulo.bloqueio).replace(/_/g, ' ')}
                            </span>
                            {titulo.correcao && <p className="text-[11px] text-slate-500">{titulo.correcao}</p>}
                          </div>
                        ) : (
                          <span className="inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">
                            elegível
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* PRÉVIA */}
      <Dialog open={previaAberta} onOpenChange={setPreviaAberta}>
        <DialogContent className="max-w-3xl" data-testid="fiscal-previa-dialog">
          <DialogHeader>
            <DialogTitle>Prévia da emissão</DialogTitle>
            <DialogDescription>
              {previa?.total || 0} nota(s) · R$ {brl(previa?.valor_total)}
              {previa?.discriminacao_manual && ' · discriminação manual aplicada a todas'}
            </DialogDescription>
          </DialogHeader>
          <DataEmissaoSegura emissao={previa?.emissao} valor={dataEmissao} onChange={setDataEmissao} />
          <div className="max-h-[45vh] overflow-y-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Aluno</TableHead>
                  <TableHead>Tomador</TableHead>
                  <TableHead>Comp.</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Discriminação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(previa?.itens || []).map(i => (
                  <TableRow key={i.titulo_id}>
                    <TableCell className="text-xs font-medium">{i.aluno_nome}</TableCell>
                    <TableCell className="text-xs">{i.tomador_nome}</TableCell>
                    <TableCell className="text-xs">{i.competencia || '-'}</TableCell>
                    <TableCell className="text-right text-xs">R$ {brl(i.valor)}</TableCell>
                    <TableCell className="text-xs text-slate-600">{i.discriminacao}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviaAberta(false)} data-testid="previa-cancelar">
              Cancelar
            </Button>
            <Button onClick={confirmarEmissao} disabled={emitindo || emitindoMes || !(previa?.total > 0) || !!erroDataEmissao(dataEmissao, previa?.emissao)}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="previa-confirmar">
              {(emitindo || emitindoMes) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Confirmar e emitir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RESULTADO */}
      <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resultado da emissão</DialogTitle>
          </DialogHeader>
          {resultadoEmissao && (
            <div className="space-y-4" data-testid="fiscal-resultado-emissao">
              {resultadoEmissao.sucesso?.length > 0 && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">{resultadoEmissao.sucesso.length} nota(s) criada(s)</span>
                  </div>
                  {resultadoEmissao.transmissao ? (
                    <p className="text-sm text-green-700">
                      {resultadoEmissao.transmissao.autorizadas} autorizada(s) ·{' '}
                      {resultadoEmissao.transmissao.rejeitadas} rejeitada(s) ·{' '}
                      {resultadoEmissao.transmissao.aguardando} aguardando retorno.
                    </p>
                  ) : (
                    <p className="text-sm text-green-600">As notas foram adicionadas à fila de emissão.</p>
                  )}
                </div>
              )}
              {resultadoEmissao.credenciais && !resultadoEmissao.credenciais.configurado && (
                <div className="p-4 bg-amber-50 rounded-lg" data-testid="fiscal-credenciais-faltando">
                  <div className="flex items-center gap-2 text-amber-800 mb-1">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Transmissão bloqueada: credenciais ausentes</span>
                  </div>
                  <p className="text-sm text-amber-700">{resultadoEmissao.credenciais.requisito}</p>
                </div>
              )}
              {resultadoEmissao.erros?.length > 0 && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700 mb-2">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">{resultadoEmissao.erros.length} erro(s)</span>
                  </div>
                  <ul className="text-sm text-red-600 space-y-1">
                    {resultadoEmissao.erros.slice(0, 6).map((e, i) => (
                      <li key={i}>• {e.aluno_nome ? `${e.aluno_nome}: ` : ''}{e.erro}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDialogAberto(false)} data-testid="resultado-fechar">Fechar</Button>
            <Button variant="outline" onClick={() => navigate('/fiscal/notas')} data-testid="resultado-ver-notas">
              Ver notas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
